import type {
  SearchApiRequestOptions,
  SearchApiResponse,
  SearchSeoApiRequestOptions,
  SearchSeoApiResponse,
  SearchProductApiRequestOptions,
  SearchProductApiResponse,
  SearchApiRequestBody,
} from './types';

const headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export const makeRequestBody = (options: SearchApiRequestOptions) => {
  const { highlight, locale, ...otherOptions } = options;
  const highlightList = highlight?.split(',');

  return {
    ...otherOptions,
    searchId: undefined,
    minAvailCode: 2,
    specialArticles: highlightList
      ? highlightList.map((highlightItem, index) => ({
          articleNumber: highlightItem,
          onPage: true,
          position: 0,
          showOnAllPages: false,
          sizeX: index === 0 ? 2 : 1,
          sizeY: 1,
        }))
      : undefined,
    version: 22,
    // used for tracking according to https://gitlab.com/empiriecom/search/fragment-search/-/blob/develop/src/interfaces/ApiParams.tsx#L17
    previousRequest: undefined,
    noLegacyEsi: true,
    // locale is overridden because API expects underscores instead of hyphens
    locale: locale?.replace('-', '_'),
  } satisfies SearchApiRequestBody;
};

/**
 * Search api data fetcher
 * @param url search api url
 * @param options request options for the search request
 * @returns json response
 */
export const searchFetcher = async ([url, options]: [
  url: string,
  options: SearchApiRequestOptions,
]) => {
  const body = makeRequestBody(options);

  const res = await fetch(url, {
    headers,
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    // throw error when response status not ok -> error boundary catches and logs the error
    throw new Error(`Endpoint: ${res.url} - Status ${res.status}: ${res.statusText}`, {
      cause: { statusCode: res.status },
    });
  }

  const json = await res.json();

  return json as Promise<SearchApiResponse>;
};

/**
 * Search seo api data fetcher
 * @param url search api url
 * @param options request options for the search seo request
 * @returns json response
 */
export const searchSeoFetcher = ([url, { locale, ...otherOptions }]: [
  url: string,
  options: SearchSeoApiRequestOptions,
]) => {
  const query = new URLSearchParams({
    ...otherOptions,
    locale: locale?.replace('-', '_') ?? 'de_DE',
  }).toString();

  const urlWithQuery = `${url}?${query}`;

  return fetch(urlWithQuery, {
    headers,
    method: 'GET',
  }).then((res) => {
    if (!res.ok) {
      // throw error when response status not ok -> error boundary catches and logs the error
      throw new Error(`Endpoint: ${res.url} - Status ${res.status}: ${res.statusText}`);
    }

    return res.json() as Promise<SearchSeoApiResponse>;
  });
};

/**
 * Search filter api data fetcher
 * @param url search filter api url
 * @param options request options for the search filter request
 * @returns json response
 */
export const searchProductFetcher = ([url, options]: [
  url: string,
  options: SearchProductApiRequestOptions,
]) =>
  fetch(url, {
    headers,
    method: 'POST',
    body: JSON.stringify(options),
  }).then((res) => {
    if (!res.ok) {
      // throw error when response status not ok -> error boundary catches and logs the error
      throw new Error(`Endpoint: ${res.url} - Status ${res.status}: ${res.statusText}`);
    }

    return res.json() as Promise<SearchProductApiResponse>;
  });
